import { useState, useEffect, useCallback, useRef } from 'react';
import {
  DefaultBrowserBehavior,
  type MeetingSession,
  type DeviceChangeObserver,
} from 'amazon-chime-sdk-js';
import { toast } from 'sonner';
import i18n from 'i18next';
import type { DeviceInfo } from '../types';

/**
 * Check if the browser supports setSinkId for audio output device selection.
 * Uses the Chime SDK's browser behavior detection which is more accurate than
 * checking prototype presence (iOS Chrome has the API but requires user gesture).
 * @see https://github.com/aws/amazon-chime-sdk-js/blob/main/guides/07_FAQs.md
 */
const browserBehavior = new DefaultBrowserBehavior();
function supportsSinkId(): boolean {
  return browserBehavior.supportsSetSinkId();
}

interface InitialSelectedDevices {
  camera: string | null;
  microphone: string | null;
  speaker: string | null;
}

interface UseDevicesProps {
  meetingSession: MeetingSession | null;
  initialSelectedDevices?: InitialSelectedDevices;
  isVideoOn?: boolean;
}

interface UseDevicesReturn {
  cameras: DeviceInfo[];
  microphones: DeviceInfo[];
  speakers: DeviceInfo[];
  selectedCamera: string | null;
  selectedMic: string | null;
  selectedSpeaker: string | null;
  selectCamera: (deviceId: string) => Promise<void>;
  selectMic: (deviceId: string) => Promise<void>;
  selectSpeaker: (deviceId: string) => Promise<void>;
  refreshDevices: () => Promise<void>;
  /** Whether the browser supports audio output device selection */
  supportsSpeakerSelection: boolean;
  /**
   * Initialize audio input with the default device.
   * Call this in a click handler when using skipDeviceSelection to preserve
   * user gesture context for iOS setSinkId compatibility.
   */
  initializeAudioInput: () => Promise<void>;
}

function mapMediaDevices(devices: MediaDeviceInfo[]): DeviceInfo[] {
  return devices.map((device) => ({
    deviceId: device.deviceId,
    label: device.label || `Device ${device.deviceId.slice(0, 8)}`,
    groupId: device.groupId,
  }));
}

export function useDevices({
  meetingSession,
  initialSelectedDevices,
  isVideoOn = false,
}: UseDevicesProps): UseDevicesReturn {
  const [cameras, setCameras] = useState<DeviceInfo[]>([]);
  const [microphones, setMicrophones] = useState<DeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<DeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [selectedMic, setSelectedMic] = useState<string | null>(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState<string | null>(null);
  const initializedRef = useRef(false);

  // Refs to track selected devices for use in observer callbacks (avoids stale closures)
  const selectedCameraRef = useRef<string | null>(null);
  const selectedMicRef = useRef<string | null>(null);
  const selectedSpeakerRef = useRef<string | null>(null);

  // Keep refs in sync with state
  selectedCameraRef.current = selectedCamera;
  selectedMicRef.current = selectedMic;
  selectedSpeakerRef.current = selectedSpeaker;

  const refreshDevices = useCallback(async () => {
    if (!meetingSession) return;

    const audioVideo = meetingSession.audioVideo;

    const [videoInputs, audioInputs, audioOutputs] = await Promise.all([
      audioVideo.listVideoInputDevices(),
      audioVideo.listAudioInputDevices(),
      audioVideo.listAudioOutputDevices(),
    ]);

    setCameras(mapMediaDevices(videoInputs));
    setMicrophones(mapMediaDevices(audioInputs));
    setSpeakers(mapMediaDevices(audioOutputs));
  }, [meetingSession]);

  const selectCamera = useCallback(
    async (deviceId: string) => {
      if (!meetingSession) return;
      // Only start video input if video is currently on to avoid lighting up
      // the camera indicator unexpectedly when user switches cameras while video is off
      if (isVideoOn) {
        await meetingSession.audioVideo.startVideoInput(deviceId);
      }
      setSelectedCamera(deviceId);
    },
    [meetingSession, isVideoOn]
  );

  const selectMic = useCallback(
    async (deviceId: string) => {
      if (!meetingSession) return;
      await meetingSession.audioVideo.startAudioInput(deviceId);
      setSelectedMic(deviceId);
    },
    [meetingSession]
  );

  const selectSpeaker = useCallback(
    async (deviceId: string) => {
      if (!meetingSession || !supportsSinkId()) return;
      try {
        await meetingSession.audioVideo.chooseAudioOutput(deviceId);
        setSelectedSpeaker(deviceId);
      } catch (error) {
        // Log but don't throw - audio will continue through default device
        // This commonly fails on iOS due to user gesture requirements
        console.warn('Failed to select audio output device:', error);
        // Still update the selected state for UI consistency
        setSelectedSpeaker(deviceId);
      }
    },
    [meetingSession]
  );

  // Initialize audio input with default device - call this in a click handler
  // when using skipDeviceSelection to preserve user gesture context for iOS
  const initializeAudioInput = useCallback(async () => {
    if (!meetingSession) return;

    const audioInputs = await meetingSession.audioVideo.listAudioInputDevices();
    if (audioInputs.length > 0) {
      const deviceId = audioInputs[0].deviceId;
      await meetingSession.audioVideo.startAudioInput(deviceId);
      setSelectedMic(deviceId);
    }
  }, [meetingSession]);

  useEffect(() => {
    if (!meetingSession) {
      // Reset initialization flag when session is cleared
      initializedRef.current = false;
      return;
    }

    let isMounted = true;
    const audioVideo = meetingSession.audioVideo;

    const observer: DeviceChangeObserver = {
      audioInputsChanged: async (freshDevices) => {
        if (!freshDevices) return;
        const mapped = mapMediaDevices(freshDevices);
        setMicrophones(mapped);

        // Handle device disconnection - check if selected mic still exists
        const currentMic = selectedMicRef.current;
        if (currentMic && !freshDevices.some((d) => d.deviceId === currentMic)) {
          // Selected mic was disconnected, fall back to first available
          if (freshDevices.length > 0) {
            const fallbackId = freshDevices[0].deviceId;
            await audioVideo.startAudioInput(fallbackId);
            setSelectedMic(fallbackId);
            toast.info(
              i18n.t('videoSession.devices.micDisconnected', 'Microphone disconnected'),
              {
                description: i18n.t(
                  'videoSession.devices.switchedToDevice',
                  'Switched to {{device}}',
                  { device: freshDevices[0].label || 'default microphone' }
                ),
                duration: 4000,
              }
            );
          } else {
            setSelectedMic(null);
          }
        }
      },
      audioOutputsChanged: (freshDevices) => {
        if (!freshDevices) return;
        const mapped = mapMediaDevices(freshDevices);
        setSpeakers(mapped);

        // Handle device disconnection - check if selected speaker still exists
        const currentSpeaker = selectedSpeakerRef.current;
        if (currentSpeaker && !freshDevices.some((d) => d.deviceId === currentSpeaker)) {
          // Selected speaker was disconnected, fall back to first available
          if (freshDevices.length > 0) {
            const fallbackId = freshDevices[0].deviceId;
            setSelectedSpeaker(fallbackId);
            // Note: We don't call chooseAudioOutput here as it may fail outside user gesture
            // The browser will typically fall back to the default output automatically
            toast.info(
              i18n.t('videoSession.devices.speakerDisconnected', 'Speaker disconnected'),
              {
                description: i18n.t(
                  'videoSession.devices.switchedToDevice',
                  'Switched to {{device}}',
                  { device: freshDevices[0].label || 'default speaker' }
                ),
                duration: 4000,
              }
            );
          } else {
            setSelectedSpeaker(null);
          }
        }
      },
      videoInputsChanged: async (freshDevices) => {
        if (!freshDevices) return;
        const mapped = mapMediaDevices(freshDevices);
        setCameras(mapped);

        // Handle device disconnection - check if selected camera still exists
        const currentCamera = selectedCameraRef.current;
        if (currentCamera && !freshDevices.some((d) => d.deviceId === currentCamera)) {
          // Selected camera was disconnected, fall back to first available
          if (freshDevices.length > 0) {
            const fallbackId = freshDevices[0].deviceId;
            // Only start video input if video was on (checked via isVideoOn prop captured in closure)
            // We use the ref pattern here to get current state without recreating the observer
            setSelectedCamera(fallbackId);
            toast.info(
              i18n.t('videoSession.devices.cameraDisconnected', 'Camera disconnected'),
              {
                description: i18n.t(
                  'videoSession.devices.switchedToDevice',
                  'Switched to {{device}}',
                  { device: freshDevices[0].label || 'default camera' }
                ),
                duration: 4000,
              }
            );
          } else {
            setSelectedCamera(null);
          }
        }
      },
    };

    audioVideo.addDeviceChangeObserver(observer);

    const fetchInitialDevices = async () => {
      const [videoInputs, audioInputs, audioOutputs] = await Promise.all([
        audioVideo.listVideoInputDevices(),
        audioVideo.listAudioInputDevices(),
        audioVideo.listAudioOutputDevices(),
      ]);

      if (isMounted) {
        setCameras(mapMediaDevices(videoInputs));
        setMicrophones(mapMediaDevices(audioInputs));
        setSpeakers(mapMediaDevices(audioOutputs));

        // Only set initial device selections once to avoid overriding user choices
        if (!initializedRef.current) {
          initializedRef.current = true;

          const initialCamera =
            initialSelectedDevices?.camera ??
            (videoInputs.length > 0 ? videoInputs[0].deviceId : null);
          const initialMic =
            initialSelectedDevices?.microphone ??
            (audioInputs.length > 0 ? audioInputs[0].deviceId : null);
          const initialSpeaker =
            initialSelectedDevices?.speaker ??
            (audioOutputs.length > 0 ? audioOutputs[0].deviceId : null);

          setSelectedCamera(initialCamera);
          setSelectedMic(initialMic);
          setSelectedSpeaker(initialSpeaker);

          // Note: We intentionally do NOT call chooseAudioOutput() here.
          // On iOS/Safari, setSinkId requires a direct user gesture context.
          // Since this runs in an async effect (outside gesture context), it would
          // throw NotAllowedError. The default audio output works without calling
          // setSinkId, and users can manually select a speaker from the menu
          // (that click provides the required user gesture context).
          // @see https://github.com/aws/amazon-chime-sdk-js/blob/main/guides/07_FAQs.md
        }
      }
    };

    void fetchInitialDevices();

    return () => {
      isMounted = false;
      audioVideo.removeDeviceChangeObserver(observer);
    };
    // Note: initialSelectedDevices intentionally excluded to prevent re-selection
    // after user changes devices. The ref tracks initialization state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingSession]);

  return {
    cameras,
    microphones,
    speakers,
    selectedCamera,
    selectedMic,
    selectedSpeaker,
    selectCamera,
    selectMic,
    selectSpeaker,
    refreshDevices,
    supportsSpeakerSelection: supportsSinkId(),
    initializeAudioInput,
  };
}
